<script setup>
const props = defineProps({
  recommended: {
    type: Array,
    required: true,
  },
});

const $route = useRoute();

const paidVideoModal = ref(null);
const nextVideoIndex = ref(null);
const channelId = ref($route.query.channel);

function handleClick(index) {
  nextVideoIndex.value = index;
  const content = props.recommended[index] || props.recommended[props.recommended.length - 1];

  if (!content.watch) {
    paidVideoModal.value?.paidVideoModalIsOpen(true, {
      id: content.contentType === 'Video' ? content.urlId : content._id,
      index: index,
      type: content.contentType === 'Video' ? 'video' : 'live',
      donation: !!content.donation,
      paid: !!(content.paid || content.onlyWithSubscription),
      title: content.title,
      actorName: content?.actor?.name,
      image: content.images.sliderTV,
    });
  } else {
    paidVideoModal.value?.paidVideoModalIsOpen(false);

    const isPreview = $route.meta.playbackType === 'preview';

    if (content.contentType === 'Video') {
      navigateTo(
        {
          name: `video-${isPreview ? 'preview' : 'play'}`,
          params: { id: content.urlId },
          query: { ...(channelId.value ? { channel: channelId.value } : {}) },
        },
        { replace: true },
      );
    } else {
      navigateTo({
        name: `program-${isPreview ? 'preview' : 'play'}`,
        params: { id: content._id },
        query: { ...(channelId.value ? { channel: channelId.value } : {}) },
      });
    }
  }
}

const playPreviewTimeout = ref(null);

function initPreview(item, timeout = 300, clear = false) {
  if (playPreviewTimeout.value) clearTimeout(playPreviewTimeout.value);

  if (clear) {
    props.recommended.forEach((item) => {
      item.showPreview = false;
    });
  }

  playPreviewTimeout.value = setTimeout(() => {
    item.showPreview = true;
  }, timeout);
}

function clearPreview(item) {
  clearTimeout(playPreviewTimeout.value);
  item.showPreview = false;
}
</script>

<template>
  <div>
    <Teleport to="body">
      <PlayerModalPaidVideoModal
        ref="paidVideoModal"
        @tickets="
          navigateTo({
            name: `${
              recommended[nextVideoIndex].contentType === 'Video' ? 'video' : 'program'
            }-purchase`,
            params: { id: recommended[nextVideoIndex].urlId || recommended[nextVideoIndex]._id },
            query: { boughtByContentId: recommended[nextVideoIndex]._id },
          })
        "
        @donate="
          navigateTo({
            name: 'donate',
            query: { boughtByContentId: recommended[nextVideoIndex]._id },
          })
        "
        @subscribe="
          navigateTo({
            name: 'upgrade',
            query: { boughtByContentId: recommended[nextVideoIndex]._id },
          })
        "
        @skip="handleClick(nextVideoIndex === recommended.length - 1 ? 0 : nextVideoIndex + 1)"
      />
    </Teleport>
    <ul role="list">
      <li
        v-for="(item, index) in recommended"
        :key="item._id"
        class="group border-b-2 py-3 transition-all duration-500 ease-in-out dark:border-gray-300/25"
        @mouseleave="clearPreview(item)"
        @touchend="clearPreview(item)"
      >
        <div class="flex gap-3">
          <div
            class="relative aspect-video w-36 shrink-0 select-none transition-all duration-500"
            :class="{
              'w-56': item.showPreview,
            }"
            @mouseover="initPreview(item)"
            @touchstart="initPreview(item, 1000, true)"
          >
            <LazyMoleculePlayerMiniPreview
              :sources="
                usePlayerSources({
                  content: useContentSourceRemap({ content: item }).content,
                  preview: true,
                })
              "
              :poster="item.images.sliderTV"
              :show="item.showPreview"
              class="mini-preview related inset-0 z-20 size-full cursor-pointer object-cover"
              style="position: absolute !important"
              @click="handleClick(index)"
            >
              <template #preview="{ state }">
                <AtomImageNuxtImage
                  class="z-30 aspect-video w-full cursor-pointer object-cover"
                  :src="item.images.sliderTV"
                  resize="450x290"
                  @click="handleClick(index)"
                />
                <AtomLoaderCircle
                  v-if="state?.waiting"
                  class="absolute inset-1/2 hidden size-10 -translate-x-1/2 -translate-y-1/2 group-hover:block"
                />
              </template>
            </LazyMoleculePlayerMiniPreview>
            <div
              class="absolute bottom-1 right-1 z-30 flex w-fit items-center gap-x-1 rounded-lg bg-[#1D1D1DB2]/70 px-2 text-sm text-white/70"
            >
              <svg
                class="size-3"
                version="1.1"
                xmlns="http://www.w3.org/2000/svg"
                width="32"
                height="32"
                viewBox="0 0 32 32"
              >
                <path
                  stroke="#fff"
                  stroke-width="3"
                  stroke-miterlimit="4"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M15.999 29.334c7.364 0 13.333-5.969 13.333-13.333s-5.969-13.333-13.333-13.333c-7.364 0-13.333 5.97-13.333 13.333s5.97 13.333 13.333 13.333z"
                />
                <path
                  stroke="#fff"
                  stroke-width="3"
                  stroke-miterlimit="4"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M20 21.333l-3.219-3.219c-0.5-0.5-0.781-1.178-0.781-1.885v-8.229"
                />
              </svg>
              {{ useTimeBeautifier(item.duration / 1000) }}
            </div>
          </div>
          <div class="flex w-full">
            <div class="flex flex-1 flex-col items-start justify-start">
              <p
                class="line-clamp-1 text-xl font-semibold text-[#3B5162] transition-all duration-500 dark:text-white"
                :class="{
                  'line-clamp-2': item.showPreview,
                }"
              >
                {{ item.actor.name }}
              </p>
              <p
                class="text-md line-clamp-2 font-normal leading-5 text-[#3B5162]/60 transition-all duration-500 dark:text-white/60"
                :class="{
                  'line-clamp-2': item.showPreview,
                }"
              >
                {{ item.title }}
              </p>
            </div>

            <div class="flex items-start gap-2">
              <MoleculeActionDonate
                :horizontal-drop-dawn="true"
                :artists="item.actors"
                :meeting-creator="
                  item.sourceType === 'meeting'
                    ? { name: item.actor.mainName || item.actor.name, id: item.creator.id }
                    : null
                "
              >
                <template #donation-icon>
                  <AtomImage draggable="false" src="/images/ceek-coin.webp" class="size-6" />
                </template>
              </MoleculeActionDonate>

              <MoleculeActionLike
                :item-id="item._id"
                :liked-by-user="item.likedByUser"
                content-type="video"
              />
              <MoleculeActionShare />
            </div>
          </div>
        </div>
      </li>
    </ul>
  </div>
</template>

<style lang="scss">
.mini-preview {
  .video-js {
    transform: translateY(-100%) !important;
  }
}
</style>
